import { useRef, RefObject } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ThunkDispatch } from '@reduxjs/toolkit'

import { editPhotoActions } from '../../../reducers/editPhoto'
import { cursorStyle } from '../../../utils/cursorStyles'

interface UseDrawingEventsReturn {
    handleMouseDown: (e: any) => void
    handleMouseMove: (e: any) => void
    handleMouseUp: () => void
    showCursor: () => void
    hideCursor: () => void
    isDrawing: RefObject<boolean>
}

const FULL_WINDOW_DRAW_DELAY = 100

/**
 * 2点間の距離を計算
 */
function getDistance(x1: number, y1: number, x2: number, y2: number): number {
    return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2)
}

/**
 * 2点間を線形補間してポイントを生成
 * @param lastX 前のポイントのX座標
 * @param lastY 前のポイントのY座標
 * @param newX 新しいポイントのX座標
 * @param newY 新しいポイントのY座標
 * @param step 補間間隔
 * @returns 補間されたポイントの配列 [x1, y1, x2, y2, ...]
 */
function interpolatePoints(
    lastX: number,
    lastY: number,
    newX: number,
    newY: number,
    step: number
): number[] {
    const distance = getDistance(lastX, lastY, newX, newY)

    // 距離が補間間隔以下なら補間不要、新しいポイントのみ返す
    if (distance <= step) {
        return [newX, newY]
    }

    const interpolatedPoints: number[] = []
    const steps = Math.ceil(distance / step)

    // 中間ポイントを生成（最初のポイントは前回と重複するので i=1 から開始）
    for (let i = 1; i <= steps; i++) {
        const t = i / steps
        interpolatedPoints.push(
            lastX + (newX - lastX) * t,
            lastY + (newY - lastY) * t
        )
    }

    return interpolatedPoints
}

/**
 * 描画イベント（マウスダウン、マウスムーブ、マウスアップ）を管理するカスタムHook
 */
export function useDrawingEvents(stageWrapRef: RefObject<HTMLDivElement>): UseDrawingEventsReturn {
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>()
    const isDrawing = useRef(false)

    const {
        lines,
        curHead,
        curHeadSize,
        isAIEraserMode,
        tempAIEraserLines,
    } = useSelector((state: any) => state.editPhoto)

    const handleMouseDown = (e: any) => {
        isDrawing.current = true
        const pos = e.target.getStage().getPointerPosition()

        if (isAIEraserMode) {
            // AI消しゴムモードの場合、一時的な線として追加（既存の線は保持）
            // drawPreviewImageは呼ばない（オーバーレイStageでReact-Konvaが自動レンダリング）
            dispatch(
                editPhotoActions.setTempAIEraserLines([
                    ...tempAIEraserLines,
                    {
                        head: curHead,
                        headSize: curHeadSize,
                        points: [pos.x, pos.y, pos.x, pos.y],
                        isAIEraser: true,
                    },
                ])
            )
        } else {
            // 通常の消しゴムモード
            // drawPreviewImageは呼ばない（オーバーレイStageでReact-Konvaが自動レンダリング）
            const newLines = [
                ...lines,
                {
                    head: curHead,
                    headSize: curHeadSize,
                    points: [pos.x, pos.y, pos.x, pos.y],
                },
            ]
            dispatch(editPhotoActions.setLines(newLines))
        }
    }

    const handleMouseMove = (e: any) => {
        // 描画中でなければスキップ
        if (!isDrawing.current) {
            return
        }

        const stage = e.target.getStage()
        const point = stage.getPointerPosition()

        if (isAIEraserMode) {
            // AI消しゴムモードの場合、一時的な線にポイントを追加
            const lastLine = tempAIEraserLines[tempAIEraserLines.length - 1]
            if (lastLine) {
                // 前のポイントを取得
                const lastX = lastLine.points[lastLine.points.length - 2]
                const lastY = lastLine.points[lastLine.points.length - 1]

                // ブラシサイズの半分を補間間隔とする
                const step = lastLine.headSize / 2
                const interpolatedPoints = interpolatePoints(lastX, lastY, point.x, point.y, step)

                const updatedLine = {
                    ...lastLine,
                    points: [...lastLine.points, ...interpolatedPoints]
                }
                // 最後の線を更新、他の線は保持
                const newLines = [...tempAIEraserLines.slice(0, -1), updatedLine]
                dispatch(editPhotoActions.setTempAIEraserLines(newLines))
            }
        } else {
            // 通常の消しゴムモード
            const lastLine = lines[lines.length - 1]
            if (lastLine) {
                // 前のポイントを取得
                const lastX = lastLine.points[lastLine.points.length - 2]
                const lastY = lastLine.points[lastLine.points.length - 1]

                // ブラシサイズの半分を補間間隔とする
                const step = lastLine.headSize / 2
                const interpolatedPoints = interpolatePoints(lastX, lastY, point.x, point.y, step)

                dispatch(editPhotoActions.addPointsToLastLine(interpolatedPoints))
            }
        }

    }

    const handleMouseUp = () => {
        isDrawing.current = false

        if (isAIEraserMode && tempAIEraserLines.length > 0) {
            // AI消しゴムモードの場合、一時的な線を確定済みの線に移動
            dispatch(editPhotoActions.applyAIEraserLines())
            // 履歴に保存
            setTimeout(() => {
                dispatch(editPhotoActions.pushHistoryIfChanged())
                dispatch(editPhotoActions.drawPreviewImage({}))
            }, FULL_WINDOW_DRAW_DELAY)
        } else if (!isAIEraserMode && lines.length > 0) {
            // 通常の消しゴムモード
            // 描画完了後に現在の状態を履歴に保存（ただし重複を避ける）
            setTimeout(() => {
                dispatch(editPhotoActions.pushHistoryIfChanged())
                dispatch(editPhotoActions.drawPreviewImage({}))
            }, FULL_WINDOW_DRAW_DELAY)
        }
    }

    const showCursor = () => {
        if (stageWrapRef.current) {
            stageWrapRef.current.style.cursor = cursorStyle(curHead, curHeadSize)
        }
    }

    const hideCursor = () => {
        if (stageWrapRef.current) {
            stageWrapRef.current.style.cursor = 'default'
        }
    }

    return {
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        showCursor,
        hideCursor,
        isDrawing,
    }
}
